import React, { useState } from 'react'

import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormGroup,
    Switch,
    Toolbar,
    Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'

import AuthService from '../../service'

// Prefixos das chaves de cache gravadas pelos controllers do backend.
// Mantém alinhado com os `cacheKey` definidos em cada *Controller.js da API.
const CACHE_TYPES = [
    { label: 'Vendas', prefix: 'sales-' },
    { label: 'Produtos', prefix: 'products-' },
    { label: 'Clientes', prefix: 'customer-' },
    { label: 'Auditoria: Itens cancelados', prefix: 'checking-v2-' },
    { label: 'Financeiro', prefix: 'financial-' },
    { label: 'Integração de pedidos', prefix: 'orderIntegration-' },
    { label: 'Exclusões de pagamentos', prefix: 'paymentExclusion-' },
    { label: 'Performance das unidades', prefix: 'performance-' },
    { label: 'Tempo por status', prefix: 'time-by-status-' },
]

export default function AdminCache() {
    const { enqueueSnackbar } = useSnackbar()
    const service = new AuthService()

    const [selected, setSelected] = useState({})
    const [allUnidades, setAllUnidades] = useState(false)
    const [loading, setLoading] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const selectedPrefixes = CACHE_TYPES
        .filter((type) => selected[type.prefix])
        .map((type) => type.prefix)

    const allChecked = selectedPrefixes.length === CACHE_TYPES.length
    const someChecked = selectedPrefixes.length > 0 && !allChecked

    function handleToggle(prefix) {
        setSelected((prev) => ({ ...prev, [prefix]: !prev[prefix] }))
    }

    function handleToggleAll() {
        if (allChecked) {
            setSelected({})
        } else {
            const next = {}
            CACHE_TYPES.forEach((type) => {
                next[type.prefix] = true
            })
            setSelected(next)
        }
    }

    function buildQuery(prefix) {
        const params = new URLSearchParams({ prefix })
        // O backend trata qualquer valor presente em `all` como verdadeiro,
        // por isso só enviamos o parâmetro quando a limpeza é global.
        if (allUnidades) {
            params.set('all', 'true')
        }
        return params.toString()
    }

    async function handleConfirm() {
        setConfirmOpen(false)
        setLoading(true)

        try {
            let totalDeleted = 0

            for (const prefix of selectedPrefixes) {
                const response = await service.delete(`/admin/cache/clear-cache?${buildQuery(prefix)}`)
                const body = await response.json().catch(() => ({}))
                totalDeleted += body?.deleted || 0
            }

            enqueueSnackbar(
                `Cache limpo com sucesso. ${totalDeleted} chave(s) removida(s).`,
                { variant: 'success' },
            )
            setSelected({})
        } catch (err) {
            const status = err?.response?.status
            const message = status === 403
                ? 'Acesso negado. Esta ação é restrita a administradores.'
                : 'Erro ao limpar o cache. Tente novamente.'

            enqueueSnackbar(message, { variant: 'error' })
            console.error('Erro ao limpar cache:', err)
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <Toolbar />

            <Container maxWidth="md" disableGutters>
                <Card>
                    <CardHeader
                        avatar={<DeleteSweepIcon color="primary" />}
                        title="Gerenciamento de cache"
                        subheader="Remova as consultas armazenadas em cache. Disponível apenas para administradores."
                        titleTypographyProps={{
                            variant: 'h5',
                            component: 'h2',
                            style: { textAlign: 'left' },
                        }}
                    />

                    <Divider />

                    <CardContent>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            Esta ação remove permanentemente os resultados em cache.
                            As próximas consultas serão recalculadas a partir do banco de dados.
                        </Alert>

                        <Typography variant="subtitle1" gutterBottom>
                            Selecione os tipos de cache que deseja limpar:
                        </Typography>

                        <FormGroup sx={{ mb: 2 }}>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={allChecked}
                                        indeterminate={someChecked}
                                        onChange={handleToggleAll}
                                        disabled={loading}
                                    />
                                )}
                                label={<strong>Todos os tipos</strong>}
                            />

                            <Divider sx={{ my: 1 }} />

                            {CACHE_TYPES.map((type) => (
                                <FormControlLabel
                                    key={type.prefix}
                                    control={(
                                        <Checkbox
                                            checked={!!selected[type.prefix]}
                                            onChange={() => handleToggle(type.prefix)}
                                            disabled={loading}
                                        />
                                    )}
                                    label={type.label}
                                />
                            ))}
                        </FormGroup>

                        <Divider sx={{ my: 2 }} />

                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={allUnidades}
                                    onChange={(event) => setAllUnidades(event.target.checked)}
                                    disabled={loading}
                                />
                            )}
                            label="Limpar o cache de todas as unidades"
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {allUnidades
                                ? 'O cache será removido para todas as unidades.'
                                : 'O cache será removido apenas para a sua unidade.'}
                        </Typography>

                        <Box display="flex" alignItems="center" gap={2}>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DeleteSweepIcon />}
                                disabled={loading || selectedPrefixes.length === 0}
                                onClick={() => setConfirmOpen(true)}
                            >
                                {loading ? 'Limpando...' : 'Limpar cache'}
                            </Button>

                            {selectedPrefixes.length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {selectedPrefixes.length} tipo(s) selecionado(s)
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Container>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirmar limpeza de cache</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`Deseja realmente limpar ${selectedPrefixes.length} tipo(s) de cache `}
                        {allUnidades ? 'de todas as unidades' : 'da sua unidade'}
                        ? Esta ação não pode ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
                    <Button onClick={handleConfirm} color="error" variant="contained" autoFocus>
                        Limpar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
